import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Compass, X } from "lucide-react";

/**
 * GuidedTour — spotlight walkthrough. Steps target live elements by CSS selector
 * (or a list of candidates, first visible one wins); a step with no visible match
 * renders as a centred card. A step may carry `route`: the tour navigates there first
 * and waits for the target to render before it measures. `maxHeight` clips the cutout
 * to the top of a tall target (a table shows its header and first rows, not the whole page).
 */
const findTarget = (selector) => {
  if (!selector) return null;
  for (const sel of Array.isArray(selector) ? selector : [selector]) {
    for (const el of document.querySelectorAll(sel)) {
      const r = el.getBoundingClientRect();
      if (r.width && r.height) return el;
    }
  }
  return null;
};
const TOP_CLEARANCE = 96; // the shell's sticky top bar, plus breathing room

export function GuidedTour({ steps, open, onClose, onFinish }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardH, setCardH] = useState(230);
  const cardRef = useRef(null);
  const step = steps[i];
  const nav = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => { if (open) setI(0); }, [open]);
  useLayoutEffect(() => { if (open && cardRef.current) setCardH(cardRef.current.offsetHeight); }, [open, step, rect]);

  const measure = useCallback(() => {
    if (!open || !step) return;
    const el = findTarget(step.selector);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) { setRect(null); return; }
    const height = step.maxHeight ? Math.min(r.height, step.maxHeight) : r.height;
    setRect({ top: r.top, left: r.left, width: r.width, height });
  }, [open, step]);

  useLayoutEffect(() => {
    if (!open || !step) return;
    if (step.route && pathname !== step.route) { setRect(null); nav(step.route); return; }
    let tries = 0;
    let t;
    const settle = () => {
      const el = findTarget(step.selector);
      if (!el && step.selector && tries++ < 12) { t = setTimeout(settle, 200); return; }
      if (el) {
        const tall = el.getBoundingClientRect().height > window.innerHeight * 0.5 || step.maxHeight;
        el.style.scrollMarginTop = `${TOP_CLEARANCE}px`;
        el.scrollIntoView({ behavior: "smooth", block: tall ? "start" : "center" });
      }
      t = setTimeout(measure, 380);
    };
    // ScrollToTop resets the window in a rAF after a route change — settle after it has fired.
    t = setTimeout(settle, step.route ? 180 : 0);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
  }, [open, step, measure, pathname, nav]);

  const next = () => (i < steps.length - 1 ? setI(i + 1) : onFinish?.());

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft" && i > 0) setI(i - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!open || !step) return null;

  const pad = step.pad ?? 8;
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const cardW = Math.min(392, vw - 32);
  let cardStyle = { left: "50%", top: "50%", transform: "translate(-50%, -50%)" };
  if (rect) {
    const gap = pad + 14;
    const clampTop = (t) => Math.min(Math.max(16, t), vh - cardH - 16);
    const centred = Math.min(Math.max(16, rect.left + rect.width / 2 - cardW / 2), vw - cardW - 16);
    const below = rect.top + rect.height + gap;
    const above = rect.top - gap - cardH;
    if (below + cardH <= vh - 16) cardStyle = { left: centred, top: below };
    else if (above >= 16) cardStyle = { left: centred, top: above };
    else if (rect.left + rect.width + gap + cardW <= vw - 16) cardStyle = { left: rect.left + rect.width + gap, top: clampTop(rect.top) };
    else if (rect.left - gap - cardW >= 16) cardStyle = { left: rect.left - gap - cardW, top: clampTop(rect.top) };
    else cardStyle = { left: centred, top: vh - cardH - 16 };
  }
  const mask = "absolute bg-[#020617]/[0.72] transition-all duration-300";

  return createPortal(
    <div className="fixed inset-0 z-[120]" data-testid="guided-tour">
      {rect ? (
        <>
          {/* Four masks instead of one overlay so the spotlit control stays clickable */}
          <div className={`${mask} left-0 right-0 top-0`} style={{ height: Math.max(0, rect.top - pad) }} onClick={onClose} />
          <div className={`${mask} left-0 right-0`} style={{ top: rect.top + rect.height + pad, bottom: 0 }} onClick={onClose} />
          <div className={`${mask} left-0`} style={{ top: rect.top - pad, height: rect.height + pad * 2, width: Math.max(0, rect.left - pad) }} onClick={onClose} />
          <div className={`${mask} right-0`} style={{ top: rect.top - pad, height: rect.height + pad * 2, left: rect.left + rect.width + pad }} onClick={onClose} />
          <div
            className="absolute rounded-xl pointer-events-none transition-all duration-300 ease-out"
            data-testid="tour-spotlight"
            style={{
              top: rect.top - pad, left: rect.left - pad,
              width: rect.width + pad * 2, height: rect.height + pad * 2,
              boxShadow: "0 0 0 2px #0055FF, 0 0 0 6px rgba(0,85,255,0.22)",
            }}
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-[#020617]/[0.72]" onClick={onClose} />
      )}

      <div
        ref={cardRef}
        className="absolute rounded-2xl border border-white/10 bg-[#0B1220] text-white p-5 shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        style={{ ...cardStyle, width: cardW }}
        data-testid={`tour-step-${i}`}
      >
        <div className="flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#7da9ff]">
            <Compass className="size-3" /> {step.eyebrow || "Guided first deal"}
          </span>
          <span className="text-[10.5px] font-mono tabular-nums text-white/40" data-testid="tour-progress">{i + 1} / {steps.length}</span>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors" data-testid="tour-skip" aria-label="Skip tour">
            <X className="size-4" />
          </button>
        </div>
        <div className="mt-2.5 text-[16px] font-semibold tracking-tight">{step.title}</div>
        <p className="mt-1.5 text-[13px] leading-relaxed text-white/65">{step.body}</p>

        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-3">
          <div className="flex items-center gap-1.5 shrink-0">
            {steps.map((_, n) => (
              <span key={n} className={`h-1.5 rounded-full transition-all duration-300 ${n === i ? "w-5 bg-[#0055FF]" : "w-1.5 bg-white/20"}`} />
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {i > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setI(i - 1)} className="h-8 px-2.5 text-white/70 hover:text-white hover:bg-white/10" data-testid="tour-back">
                <ArrowLeft className="size-3.5 mr-1" /> Back
              </Button>
            )}
            <Button size="sm" onClick={next} className="h-8 bg-[#0055FF] hover:bg-[#0044CC] font-semibold whitespace-nowrap" data-testid="tour-next">
              {i === steps.length - 1 ? (step.finishLabel || "Let's go") : "Next"} <ArrowRight className="size-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
